import { useRef, useState } from 'react';
import './UsageChart.css';

const WIDTH = 640;
const HEIGHT = 220;
const MARGIN = { top: 16, right: 12, bottom: 28, left: 44 };
const PLOT_WIDTH = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_HEIGHT = HEIGHT - MARGIN.top - MARGIN.bottom;

function niceCeil(value) {
  if (value <= 0) return 10;
  const exponent = Math.floor(Math.log10(value));
  const magnitude = 10 ** exponent;
  const residual = value / magnitude;
  const niceResidual = residual <= 1 ? 1 : residual <= 2 ? 2 : residual <= 5 ? 5 : 10;
  return niceResidual * magnitude;
}

function monthLabel(yearMonth) {
  const [year, month] = yearMonth.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString(undefined, { month: 'short' });
}

/**
 * Single-series "tokens used per month" trend chart (line + area wash), with
 * a per-point hover/focus tooltip and a hidden accessible table fallback.
 */
export default function UsageChart({ data }) {
  const svgRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(null);

  const maxValue = niceCeil(Math.max(...data.map((d) => d.tokensUsed), 1));
  const yTicks = [0, maxValue / 2, maxValue];

  const xFor = (i) => (data.length > 1 ? MARGIN.left + (PLOT_WIDTH * i) / (data.length - 1) : MARGIN.left + PLOT_WIDTH / 2);
  const yFor = (v) => MARGIN.top + PLOT_HEIGHT - (v / maxValue) * PLOT_HEIGHT;

  const linePath = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${xFor(i)},${yFor(d.tokensUsed)}`).join(' ');
  const areaPath = `${linePath} L${xFor(data.length - 1)},${MARGIN.top + PLOT_HEIGHT} L${xFor(0)},${MARGIN.top + PLOT_HEIGHT} Z`;

  function nearestIndexForClientX(clientX) {
    const rect = svgRef.current.getBoundingClientRect();
    const relativeX = ((clientX - rect.left) / rect.width) * WIDTH;
    let nearest = 0;
    let nearestDist = Infinity;
    data.forEach((_, i) => {
      const dist = Math.abs(xFor(i) - relativeX);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = i;
      }
    });
    return nearest;
  }

  const active = activeIndex != null ? data[activeIndex] : null;

  return (
    <div className="usage-chart">
      <svg
        ref={svgRef}
        className="usage-chart-svg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Tokens used per month in ${data[0]?.month.split('-')[0]}`}
        onPointerMove={(e) => setActiveIndex(nearestIndexForClientX(e.clientX))}
        onPointerLeave={() => setActiveIndex(null)}
      >
        {yTicks.map((tick) => (
          <g key={tick}>
            <line
              className="usage-chart-gridline"
              x1={MARGIN.left}
              x2={WIDTH - MARGIN.right}
              y1={yFor(tick)}
              y2={yFor(tick)}
            />
            <text className="usage-chart-axis-label" x={MARGIN.left - 8} y={yFor(tick)} textAnchor="end" dy="0.32em">
              {Math.round(tick).toLocaleString()}
            </text>
          </g>
        ))}

        <path className="usage-chart-area" d={areaPath} />
        <path className="usage-chart-line" d={linePath} />

        {data.map((d, i) => (
          <text
            key={`x-${d.month}`}
            className="usage-chart-axis-label"
            x={xFor(i)}
            y={HEIGHT - 8}
            textAnchor="middle"
          >
            {monthLabel(d.month)}
          </text>
        ))}

        {activeIndex != null && (
          <line
            className="usage-chart-crosshair"
            x1={xFor(activeIndex)}
            x2={xFor(activeIndex)}
            y1={MARGIN.top}
            y2={MARGIN.top + PLOT_HEIGHT}
          />
        )}

        {data.map((d, i) => (
          <circle
            key={d.month}
            className={`usage-chart-point ${activeIndex === i ? 'usage-chart-point--active' : ''}`}
            cx={xFor(i)}
            cy={yFor(d.tokensUsed)}
            r={activeIndex === i ? 6 : 4}
            tabIndex={0}
            role="button"
            aria-label={`${monthLabel(d.month)}: ${d.tokensUsed.toLocaleString()} tokens`}
            onFocus={() => setActiveIndex(i)}
            onBlur={() => setActiveIndex(null)}
          />
        ))}

        {data.length > 0 && (
          <text
            className="usage-chart-end-label"
            x={xFor(data.length - 1)}
            y={yFor(data[data.length - 1].tokensUsed) - 12}
            textAnchor="end"
          >
            {data[data.length - 1].tokensUsed.toLocaleString()}
          </text>
        )}
      </svg>

      {active && (
        <div
          className="usage-chart-tooltip"
          style={{ left: `${(xFor(activeIndex) / WIDTH) * 100}%`, top: `${(yFor(active.tokensUsed) / HEIGHT) * 100}%` }}
        >
          <div className="usage-chart-tooltip-value">{active.tokensUsed.toLocaleString()} tokens</div>
          <div className="usage-chart-tooltip-label">{monthLabel(active.month)}</div>
        </div>
      )}

      <table className="usage-chart-table sr-only">
        <caption>Tokens used per month</caption>
        <thead>
          <tr>
            <th scope="col">Month</th>
            <th scope="col">Tokens used</th>
          </tr>
        </thead>
        <tbody>
          {data.map((d) => (
            <tr key={d.month}>
              <td>{monthLabel(d.month)}</td>
              <td>{d.tokensUsed.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
